// Every call the phones logged, newest first, with its recording where there is one (D-077): for checking what was
// synced, what each recording was matched to, and how far it got. A recording still waiting for its call is listed
// too, with its file name; one Voxowa does not keep (a colleague's, an excluded or a personal number's, or one that
// never found a call) is never shown (D-179). A temporary page for owners, admins and managers.
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { RAW_CALLS_PAGE } from "../lib/api";
import { fmtDateTime, fmtDuration, fmtPhone, isUnanswered } from "../lib/format";
import type { RawCallRow } from "../lib/types";
import { useMember } from "../app/session";
import { Card, Empty, errorMessage, ErrorNote, Loading, Page, useLoad } from "../app/ui";

const STATUS: Record<string, string> = {
  done: "Processed",
  queued: "Waiting",
  held: "Waiting",
  processing: "Processing",
  failed: "Failed",
};

const MATCH: Record<string, string> = {
  call_log: "matched to the call",
  file_name: "by the number in its file name",
  contact_name: "by the contact name in its file name",
  none: "no call or customer found",
};

const DIRECTION: Record<string, string> = {
  outgoing: "Outgoing",
  incoming: "Incoming",
  missed: "Missed",
  rejected: "Rejected",
  blocked: "Blocked",
  voicemail: "Voicemail",
  unknown: "Unknown",
};

export function Calls() {
  const { api, tz, member } = useMember();
  const members = useLoad(() => api.members(), [api]);
  const [memberId, setMemberId] = useState("");
  const [rows, setRows] = useState<RawCallRow[] | null>(null);
  const [more, setMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState<{ id: string; url: string } | null>(null);

  const load = async (before: string | null) => {
    setBusy(true);
    setError(null);
    try {
      const page = await api.rawCalls({ memberId: memberId || null, before });
      setRows((prev) => (before && prev ? [...prev, ...page] : page));
      setMore(page.length >= RAW_CALLS_PAGE);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    setRows(null);
    setPlaying(null);
    void load(null);
  }, [api, memberId]);

  const play = async (recordingId: string) => {
    setError(null);
    try {
      const res = await api.playback(recordingId, 0); // Checked and audit-logged on the server each time.
      setPlaying({ id: recordingId, url: res.url });
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  const callers = (members.data ?? []).filter((m) => m.status === "active");

  return (
    <Page
      title="Calls"
      sub="Every call the phones sent, newest first, with its recording where there is one"
      actions={
        <select value={memberId} onChange={(e) => setMemberId(e.target.value)} aria-label="Caller">
          <option value="">Everyone</option>
          {callers.map((m) => <option key={m.id} value={m.id}>{m.display_name}</option>)}
        </select>
      }
    >
      <ErrorNote error={error} />
      <Card flush>
        {!rows && (
          <div style={{ padding: 16 }}>
            <Loading />
          </div>
        )}
        {rows && rows.length === 0 && <Empty>No calls yet. They appear here as the phones sync.</Empty>}
        {rows && rows.length > 0 && (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Caller</th>
                  <th>Customer</th>
                  <th>Call</th>
                  <th>Recording</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.kind}-${r.id}`}>
                    <td className="nowrap">{fmtDateTime(r.at, tz)}</td>
                    <td>{r.member_name}</td>
                    <td>
                      {r.lead_id
                        ? <Link to={`/leads/${r.lead_id}`}>{r.lead_name || fmtPhone(r.phone)}</Link>
                        : r.phone
                        ? fmtPhone(r.phone)
                        : <span className="faint">Unknown</span>}
                      {(r.lead_name || r.contact_name) && r.phone && (
                        <div className="faint">
                          {[r.contact_name && r.contact_name !== r.lead_name ? r.contact_name : null, fmtPhone(r.phone)]
                            .filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </td>
                    <td className="nowrap">
                      {r.kind === "call"
                        ? (
                          <>
                            {`${DIRECTION[r.direction ?? "unknown"] ?? r.direction} · ${fmtDuration(r.duration_s)}`}
                            {isUnanswered(r.direction, r.duration_s, r.call_status) && (
                              <span className="badge unanswered">Unanswered</span>
                            )}
                          </>
                        )
                        : <span className="faint">No call in the log</span>}
                    </td>
                    <td>
                      {r.recording
                        ? (
                          <div className="stack" style={{ gap: 4 }}>
                            <span className="row">
                              <span>
                                {r.recording.no_conversation
                                  ? "No conversation"
                                  : STATUS[r.recording.status] ?? r.recording.status}
                              </span>
                              {r.recording.playable && (
                                <button
                                  className="small"
                                  onClick={() => void play(r.recording!.id)}
                                >
                                  ▶ Play
                                </button>
                              )}
                              <Link className="button small" to={`/calls/${r.recording.id}`}>Open</Link>
                            </span>
                            {r.recording.match_method && (
                              <span className="faint">
                                {["queued", "held", "processing"].includes(r.recording.status) &&
                                    r.recording.match_method === "none"
                                  ? "waiting for its call in the phone's log (up to 15 minutes)"
                                  : MATCH[r.recording.match_method] ?? r.recording.match_method}
                              </span>
                            )}
                            {r.kind === "recording" && r.recording.file_name && (
                              <span className="faint">{r.recording.file_name}</span>
                            )}
                            {playing?.id === r.recording.id && (
                              <audio
                                controls
                                autoPlay
                                src={playing.url}
                                onError={() => setPlaying(null)}
                              />
                            )}
                          </div>
                        )
                        : r.recording_due && member.recordings
                        ? (
                          <span
                            className={`badge${r.recording_due === "missing" ? " danger" : ""}`}
                            title={r.recording_due === "missing"
                              ? "The phone synced after this call without its recording: check that call recording is on."
                              : "A call that connected: its recording comes with the phone's next sync."}
                          >
                            {r.recording_due === "missing" ? "Recording missing" : "Recording expected"}
                          </span>
                        )
                        : <span className="faint">None</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {rows && more && (
          <div style={{ padding: 16 }}>
            <button disabled={busy} onClick={() => void load(rows[rows.length - 1].at)}>Show more</button>
          </div>
        )}
      </Card>
    </Page>
  );
}
